import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faChevronDown,
  faTimes,
  faBuilding,
  faCreditCard,
  faCheck
} from '@fortawesome/free-solid-svg-icons';

const AdminAccountSelector = ({
  policies = [],
  cardGroups = [],
  selectedValue,
  onSelectionChange,
  currentUser,
  theme,
  isMobile = false,
  disabled = false,
  isLoading = false,
  closeOnScroll = true // Set to false to reposition instead of closing
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'policies', 'cardgroups'
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const isPolicyAdmin = currentUser?.isPolicyAdmin;
  const isAdmin = currentUser?.isAdmin;
  const isContractAdmin = currentUser?.isContractAdmin;

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen) {
        const isClickInTrigger = triggerRef.current && triggerRef.current.contains(event.target);
        const isClickInDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);

        if (!isClickInTrigger && !isClickInDropdown) {
          setIsOpen(false);
        }
      }
    };

    const handleScroll = (event) => {
      if (isOpen) {
        // Check if the scroll event is coming from within the dropdown
        const scrollingElement = event.target;
        const isDropdownScroll = dropdownRef.current && dropdownRef.current.contains(scrollingElement);

        // Only close/reposition if the scroll is NOT from within the dropdown
        if (!isDropdownScroll) {
          if (closeOnScroll) {
            setIsOpen(false);
          } else {
            // Throttle repositioning to avoid excessive calls
            if (scrollTimeoutRef.current) {
              clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
              calculateDropdownPosition();
            }, 10);
          }
        }
      }
    };

    const handleResize = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Filter and search logic
  const filteredItems = React.useMemo(() => {
    let items = [];

    // Add policies based on filter
    if (filterType === 'all' || filterType === 'policies') {
      const policyItems = policies.map(policy => ({
        type: 'policy',
        id: policy.ID,
        value: policy.PolicyNumber,
        displayName: policy.CompanyName || policy.Description,
        accountNumber: policy.PolicyNumber,
        searchableText: `${policy.PolicyNumber} ${policy.CompanyName || policy.Description || ''}`.toLowerCase(),
        icon: faBuilding,
        policy
      }));
      items.push(...policyItems);
    }

    // Add card groups based on filter
    if (filterType === 'all' || filterType === 'cardgroups') {
      const cardGroupItems = cardGroups.map(group => ({
        type: 'cardgroup',
        id: group.cardGroupID,
        value: group.AccountNumber,
        displayName: group.groupName,
        accountNumber: group.AccountNumber,
        searchableText: `${group.groupName} ${group.AccountNumber}`.toLowerCase(),
        icon: faCreditCard,
        cardGroup: group
      }));
      items.push(...cardGroupItems);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      items = items.filter(item =>
        item.searchableText.includes(searchLower)
      );
    }

    return items;
  }, [policies, cardGroups, filterType, searchTerm, isPolicyAdmin]);

  // Get current selection display
  const currentSelection = React.useMemo(() => {
    if (!selectedValue) return null;

    const foundPolicy = policies.find(p => p.PolicyNumber.toString() === selectedValue.toString());
    if (foundPolicy) {
      return {
        type: 'policy',
        displayName: foundPolicy.CompanyName || foundPolicy.Description,
        accountNumber: foundPolicy.PolicyNumber,
        icon: faBuilding
      };
    }

    const foundCardGroup = cardGroups.find(g => g.AccountNumber.toString() === selectedValue.toString());
    if (foundCardGroup) {
      return {
        type: 'cardgroup',
        displayName: foundCardGroup.groupName,
        accountNumber: foundCardGroup.AccountNumber,
        icon: faCreditCard
      };
    }

    return null;
  }, [selectedValue, policies, cardGroups, isPolicyAdmin]);

  const handleItemSelect = (item) => {
    const selectedCardGroup = item.type === 'cardgroup' ? item.cardGroup : null;
    const selectedPolicy = item.type === 'policy' ? item.policy : null;

    onSelectionChange({
      value: item.value,
      selectedPolicy,
      selectedCardGroup
    });

    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    searchInputRef.current?.focus();
  };

  const calculateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: isMobile ? rect.width : Math.max(rect.width, 500)
      });
    }
  };

  const toggleDropdown = () => {
    if (!disabled) {
      if (!isOpen) {
        calculateDropdownPosition();
        setSearchTerm('');
      }
      setIsOpen(!isOpen);
    }
  };

  // Count items for filter buttons
  const policiesCount = policies.length;
  const cardGroupsCount = cardGroups.length;
  const totalCount = policiesCount + cardGroupsCount;

  // Show loading state
  if (isLoading || (disabled && !currentUser)) {
    return (
      <div className={`${isMobile ? 'w-full' : ''}`}>
        <div className={`
          ${isMobile
            ? 'bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200'
            : 'bg-white rounded-md p-2 border border-gray-200'
          }
          opacity-75 cursor-wait
        `}>
          <span className="text-gray-500">
            Loading...
          </span>
        </div>
      </div>
    );
  }

  // Show no data state only after we've confirmed there's no data and user is authenticated
  if (disabled || (totalCount === 0 && currentUser)) {
    return (
      <div className={`${isMobile ? 'w-full' : ''}`}>
        <div className={`
          ${isMobile
            ? 'bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200'
            : 'bg-gray-100 rounded-md p-2'
          }
          opacity-50 cursor-not-allowed
        `}>
          <span className="text-gray-500">
            No Active Policies or Card Groups
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${isMobile ? 'w-full' : ''}`} ref={dropdownRef} style={{ zIndex: 'auto' }}>
      {/* Label for mobile */}
      {isMobile && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Account Selection
        </label>
      )}

      {/* Trigger Button */}
      <button
        ref={triggerRef}
        onClick={toggleDropdown}
        className={`
          ${isMobile
            ? 'w-full bg-white rounded-lg p-3 border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm'
            : 'bg-white rounded-md p-2 border border-gray-200 hover:border-gray-400 shadow-sm'
          }
          flex items-center justify-between transition-all duration-200 focus:outline-none
          ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}
        `}
        disabled={disabled}
      >
        <div className="flex items-center flex-1 min-w-0">
          {currentSelection && (
            <FontAwesomeIcon
              icon={currentSelection.icon}
              className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'} mr-2 text-gray-500 flex-shrink-0`}
            />
          )}
          <div className="flex-1 min-w-0 text-left">
            {currentSelection ? (
              <>
                <div className={`
                  ${isMobile ? 'text-sm' : 'text-sm'}
                  text-gray-900 font-medium truncate
                `}>
                  {currentSelection.displayName}
                </div>
                <div className={`
                  ${isMobile ? 'text-xs' : 'text-xs'}
                  text-gray-500 truncate
                `}>
                  {currentSelection.type === 'policy' ? 'Policy' : 'Account'} #{currentSelection.accountNumber}
                </div>
              </>
            ) : (
              <span className={`
                ${isMobile ? 'text-sm' : 'text-sm'}
                text-gray-500
              `}>
                Select an account
              </span>
            )}
          </div>
        </div>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`
            ${isMobile ? 'w-4 h-4' : 'w-3 h-3'}
            text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2
            ${isOpen ? 'rotate-180' : ''}
          `}
        />
      </button>

      {/* Dropdown Panel - Rendered via Portal for proper z-index layering */}
      {isOpen && !isMobile && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border border-gray-300 rounded-xl shadow-2xl max-h-[400px] overflow-hidden animate-[slideDown_0.15s_ease-out]"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width,
            zIndex: 99999,
            animation: 'slideDown 0.15s ease-out',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            backdropFilter: 'blur(8px)'
          }}
        >
          {/* Search Header */}
          <div
            className="p-4 border-b border-gray-200"
            style={{
              background: `linear-gradient(to right, ${theme.primary}08, #ffffff, ${theme.primary}08)`
            }}
          >
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: theme.primary }}
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by company name, policy number, or account number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-10 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 bg-white shadow-sm hover:shadow-md"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1.5 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            {totalCount > 5 && (
              <div className="flex mt-4 space-x-2">
                <button
                  onClick={() => setFilterType('all')}
                  className={`
                    px-5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm
                    ${filterType === 'all'
                      ? 'text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-200 bg-white'
                    }
                  `}
                  style={filterType === 'all' ? {
                    background: `linear-gradient(to right, ${theme.primary}, ${theme.primary}dd)`,
                    boxShadow: `0 4px 15px ${theme.primary}40`
                  } : {}}
                >
                  All ({totalCount})
                </button>
                {policiesCount > 0 && (
                  <button
                    onClick={() => setFilterType('policies')}
                    className={`
                      px-5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm
                      ${filterType === 'policies'
                        ? 'text-white shadow-lg transform scale-105'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-200 bg-white'
                      }
                    `}
                    style={filterType === 'policies' ? {
                      background: `linear-gradient(to right, ${theme.primary}, ${theme.primary}dd)`,
                      boxShadow: `0 4px 15px ${theme.primary}40`
                    } : {}}
                  >
                    Policies ({policiesCount})
                  </button>
                )}
                {cardGroupsCount > 0 && (
                  <button
                    onClick={() => setFilterType('cardgroups')}
                    className={`
                      px-5 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 shadow-sm
                      ${filterType === 'cardgroups'
                        ? 'text-white shadow-lg transform scale-105'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-200 bg-white'
                      }
                    `}
                    style={filterType === 'cardgroups' ? {
                      background: `linear-gradient(to right, ${theme.primary}, ${theme.primary}dd)`,
                      boxShadow: `0 4px 15px ${theme.primary}40`
                    } : {}}
                  >
                    Card Groups ({cardGroupsCount})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Results List */}
          <div className="overflow-y-auto px-2 pt-2 pb-4" style={{ maxHeight: '280px' }}>
            {filteredItems.length > 0 ? (
              <div className="space-y-1 pb-2">
                {filteredItems.map((item) => (
                  <button
                    key={`${item.type}-${item.id}`}
                    onClick={() => handleItemSelect(item)}
                    className={`
                      w-full flex items-center px-3 py-3 rounded-lg text-left transition-all duration-150 hover:shadow-md
                      ${selectedValue?.toString() === item.value?.toString()
                        ? 'border-l-4 shadow-sm'
                        : 'hover:border-l-4 hover:border-gray-300'
                      }
                    `}
                    style={{
                      ...(selectedValue?.toString() === item.value?.toString() ? {
                        background: `linear-gradient(to right, ${theme.primary}15, ${theme.primary}08)`,
                        borderLeftColor: theme.primary
                      } : {
                        background: 'transparent'
                      })
                    }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mr-3"
                      style={{
                        background: `linear-gradient(to bottom right, ${theme.primary}20, ${theme.primary}10)`
                      }}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="w-4 h-4"
                        style={{ color: theme.primary }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate mb-0.5">
                        {item.displayName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {item.type === 'policy' ? 'Policy' : 'Account'} #{item.accountNumber}
                      </div>
                    </div>
                    {selectedValue?.toString() === item.value?.toString() && (
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ml-3"
                        style={{ backgroundColor: theme.primary }}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="w-3 h-3 text-white"
                        />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <FontAwesomeIcon icon={faSearch} className="w-8 h-8 text-gray-400" />
                </div>
                <div className="text-sm font-medium text-gray-600 mb-1">
                  {searchTerm ? 'No results found' : 'No accounts available'}
                </div>
                {searchTerm && (
                  <div className="text-xs text-gray-500">
                    Try searching with different keywords
                  </div>
                )}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Mobile Dropdown - Rendered normally */}
      {isOpen && isMobile && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-xl shadow-2xl max-h-96 overflow-hidden animate-[slideDown_0.15s_ease-out]"
          style={{
            zIndex: 99999,
            animation: 'slideDown 0.15s ease-out',
            boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          {/* Search Header */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
              />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search by company name, policy number, or account number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all duration-200 bg-gray-50 focus:bg-white"
              />
              {searchTerm && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                </button>
              )}
            </div>

            {/* Filter Tabs - Mobile */}
            {totalCount > 5 && (
              <div className="flex mt-3 space-x-1 overflow-x-auto pb-1">
                <button
                  onClick={() => setFilterType('all')}
                  className={`
                    px-2 py-1 text-xs font-medium rounded-md flex-shrink-0 transition-all duration-200
                    ${filterType === 'all'
                      ? 'bg-blue-100 text-blue-700 border border-blue-200 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-transparent'
                    }
                  `}
                >
                  All ({totalCount})
                </button>
                {policiesCount > 0 && (
                  <button
                    onClick={() => setFilterType('policies')}
                    className={`
                      px-2 py-1 text-xs font-medium rounded-md flex-shrink-0 transition-all duration-200
                      ${filterType === 'policies'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-transparent'
                      }
                    `}
                  >
                    Policies ({policiesCount})
                  </button>
                )}
                {cardGroupsCount > 0 && (
                  <button
                    onClick={() => setFilterType('cardgroups')}
                    className={`
                      px-2 py-1 text-xs font-medium rounded-md flex-shrink-0 transition-all duration-200
                      ${filterType === 'cardgroups'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200 shadow-sm'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-transparent'
                      }
                    `}
                  >
                    Card Groups ({cardGroupsCount})
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Results List */}
          <div className="overflow-y-auto px-2 pt-2 pb-4" style={{ maxHeight: '200px' }}>
            {filteredItems.length > 0 ? (
              <div className="space-y-1 pb-2">
                {filteredItems.map((item) => (
                  <button
                    key={`${item.type}-${item.id}`}
                    onClick={() => handleItemSelect(item)}
                    className={`
                      w-full flex items-center px-3 py-2.5 rounded-lg text-left transition-all duration-150
                      ${selectedValue?.toString() === item.value?.toString()
                        ? 'border-l-4'
                        : 'hover:bg-gray-50'
                      }
                    `}
                    style={{
                      ...(selectedValue?.toString() === item.value?.toString() ? {
                        background: `linear-gradient(to right, ${theme.primary}15, ${theme.primary}08)`,
                        borderLeftColor: theme.primary
                      } : {})
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mr-3"
                      style={{
                        background: `linear-gradient(to bottom right, ${theme.primary}20, ${theme.primary}10)`
                      }}
                    >
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="w-3 h-3"
                        style={{ color: theme.primary }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.displayName}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {item.type === 'policy' ? 'Policy' : 'Account'} #{item.accountNumber}
                      </div>
                    </div>
                    {selectedValue?.toString() === item.value?.toString() && (
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ml-2"
                        style={{ backgroundColor: theme.primary }}
                      >
                        <FontAwesomeIcon
                          icon={faCheck}
                          className="w-2.5 h-2.5 text-white"
                        />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-3 py-6 text-center text-gray-500">
                <FontAwesomeIcon icon={faSearch} className="w-8 h-8 mb-2 opacity-50" />
                <div className="text-sm">
                  {searchTerm ? `No results found for "${searchTerm}"` : 'No accounts available'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccountSelector;